import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { Check, Pencil, X } from "lucide-react";
import { Button, Input } from "@akuna/ui";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { useSaveStayDailyRates, useStayDailyRates } from "@/hooks/useStayDailyRates";
import { SectionCard, SectionHeader } from "../accommodations/primitives";

type Props = {
  hasDaycare: boolean;
  hasHotel: boolean;
  canEditDaycare: boolean;
  canEditHotel: boolean;
};

type RateKind = "daycare" | "hotel";

export default function DailyRatesSection({ hasDaycare, hasHotel, canEditDaycare, canEditHotel }: Props) {
  const { t, i18n } = useTranslation("daycareHotel");
  const { data, isLoading } = useStayDailyRates();
  const save = useSaveStayDailyRates();
  const [editing, setEditing] = useState<RateKind | null>(null);
  const [priceCents, setPriceCents] = useState(0);
  const [priceCentsWithPlan, setPriceCentsWithPlan] = useState(0);
  const priceInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) {
      setPriceCents(0);
      setPriceCentsWithPlan(0);
    }
  }, [editing]);

  const rateFor = (kind: RateKind) => kind === "daycare" ? data?.daycareDailyRate ?? null : data?.hotelNightlyRate ?? null;
  const begin = (kind: RateKind) => {
    setEditing(kind);
    setPriceCents(Math.round((rateFor(kind) ?? 0) * 100));
    if (kind === "hotel") setPriceCentsWithPlan(Math.round((data?.hotelNightlyRateWithDaycarePlan ?? 0) * 100));
  };
  const format = (rate: number | null) => rate === null
    ? t("dailyRates.notConfigured")
    : new Intl.NumberFormat(i18n.language.startsWith("pt") ? "pt-BR" : "en-US", { style: "currency", currency: "BRL" }).format(rate);
  const saveRate = async (kind: RateKind) => {
    const parsed = priceCents > 0 ? priceCents / 100 : null;
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      toast({ variant: "destructive", title: t("dailyRates.invalidValue") });
      return;
    }
    if (kind === "daycare") {
      try {
        await save.mutateAsync({ daycareDailyRate: parsed });
        setEditing(null);
        toast({ title: t("dailyRates.updated") });
      } catch {
        toast({ variant: "destructive", title: t("dailyRates.saveFailed") });
      }
      return;
    }
    const parsedWithPlan = priceCentsWithPlan > 0 ? priceCentsWithPlan / 100 : null;
    if (parsedWithPlan !== null && (!Number.isFinite(parsedWithPlan) || parsedWithPlan <= 0)) {
      toast({ variant: "destructive", title: t("dailyRates.invalidValue") });
      return;
    }
    try {
      await save.mutateAsync({ hotelNightlyRate: parsed, hotelNightlyRateWithDaycarePlan: parsedWithPlan });
      setEditing(null);
      toast({ title: t("dailyRates.updated") });
    } catch {
      toast({ variant: "destructive", title: t("dailyRates.saveFailed") });
    }
  };

  const makePriceField = (cents: number, setCents: (next: number) => void, inputRef: RefObject<HTMLInputElement>) => {
    const applyPriceCents = (nextCents: number) => setCents(Math.max(0, Math.min(nextCents, 999999999)));
    const setCaretFromDigitsRight = (digitsRight: number) => requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      if (digitsRight <= 0) return input.setSelectionRange(input.value.length, input.value.length);
      let counted = 0;
      let position = input.value.length;
      for (let index = input.value.length - 1; index >= 0; index -= 1) {
        if (/\d/.test(input.value[index])) counted += 1;
        if (counted === digitsRight) { position = index; break; }
      }
      input.setSelectionRange(position, position);
    });
    const handlePriceKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const raw = input.value;
      const start = input.selectionStart ?? raw.length;
      const end = input.selectionEnd ?? start;
      if (event.ctrlKey || event.metaKey || event.altKey || ["Tab", "Enter", "Escape", "Home", "End"].includes(event.key) || event.key.startsWith("Arrow")) return;
      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        if (end > start) { applyPriceCents(0); setCaretFromDigitsRight(0); return; }
        let index = event.key === "Backspace" ? start - 1 : start;
        const step = event.key === "Backspace" ? -1 : 1;
        while (index >= 0 && index < raw.length && !/\d/.test(raw[index])) index += step;
        if (index < 0 || index >= raw.length) return;
        const right = raw.slice(index + (event.key === "Backspace" ? 1 : 0)).replace(/\D/g, "").length;
        const centsStr = String(cents).padStart(3, "0");
        const digit = centsStr.length - 1 - right;
        applyPriceCents(Number(centsStr.slice(0, digit) + centsStr.slice(digit + 1) || 0));
        setCaretFromDigitsRight(right);
        return;
      }
      if (event.key.length === 1 && /\d/.test(event.key)) {
        event.preventDefault();
        if (end > start) { applyPriceCents(Number(event.key)); setCaretFromDigitsRight(0); return; }
        const right = raw.slice(start).replace(/\D/g, "").length;
        const centsStr = String(cents).padStart(3, "0");
        const at = centsStr.length - right;
        applyPriceCents(Number(centsStr.slice(0, at) + event.key + centsStr.slice(at)));
        setCaretFromDigitsRight(right);
        return;
      }
      if (event.key.length === 1) event.preventDefault();
    };
    return { handlePriceKeyDown };
  };

  const hotelWithPlanInputRef = useRef<HTMLInputElement | null>(null);
  const { handlePriceKeyDown } = makePriceField(priceCents, setPriceCents, priceInputRef);
  const { handlePriceKeyDown: handlePriceWithPlanKeyDown } = makePriceField(priceCentsWithPlan, setPriceCentsWithPlan, hotelWithPlanInputRef);

  const priceInput = (ref: RefObject<HTMLInputElement>, ariaLabel: string, cents: number, onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void) => (
    <div className="flex items-baseline gap-2">
      <span className="font-display text-lg font-extrabold text-foreground">R$</span>
      <Input ref={ref} aria-label={ariaLabel} inputMode="numeric" value={cents > 0 ? (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""} onKeyDown={onKeyDown} onChange={() => {}} placeholder="0,00" className="h-10 w-36 font-display text-lg font-extrabold" />
    </div>
  );

  const daycareCard = () => {
    const rate = rateFor("daycare");
    const isEditing = editing === "daycare";
    return (
      <SectionCard className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-[15px] font-bold text-foreground">{t("dailyRates.daycare.title")}</h3>
            <p className="mt-1 text-[12.5px] text-color-light">{t("dailyRates.daycare.description")}</p>
          </div>
          {canEditDaycare && !isEditing ? <Button size="sm" variant="outline" onClick={() => begin("daycare")}><Pencil className="mr-1.5 h-3.5 w-3.5" />{t("common.edit")}</Button> : null}
        </div>
        <div className="mt-5">
          {isEditing ? priceInput(priceInputRef, t("dailyRates.daycare.title"), priceCents, handlePriceKeyDown) : <p className="font-display text-xl font-extrabold text-foreground">{format(rate)}</p>}
        </div>
        {isEditing ? <div className="mt-3 flex justify-end gap-2">
          <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => setEditing(null)}><X className="mr-1.5 h-3.5 w-3.5" />{t("common.cancel")}</Button>
          <Button size="sm" disabled={save.isPending} onClick={() => saveRate("daycare")}><Check className="mr-1.5 h-3.5 w-3.5" />{t("common.save")}</Button>
        </div> : null}
      </SectionCard>
    );
  };

  const hotelCard = () => {
    const rate = rateFor("hotel");
    const isEditing = editing === "hotel";
    return (
      <SectionCard className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-[15px] font-bold text-foreground">{t("dailyRates.hotel.title")}</h3>
            <p className="mt-1 text-[12.5px] text-color-light">{t("dailyRates.hotel.description")}</p>
          </div>
          {canEditHotel && !isEditing ? <Button size="sm" variant="outline" onClick={() => begin("hotel")}><Pencil className="mr-1.5 h-3.5 w-3.5" />{t("common.edit")}</Button> : null}
        </div>
        {/* Os dois preços lado a lado: é a comparação que a recepção faz. */}
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-[12.5px] font-semibold text-foreground">{t("dailyRates.hotel.standardLabel")}</p>
            <div className="mt-2">
              {isEditing
                ? priceInput(priceInputRef, t("dailyRates.hotel.standardLabel"), priceCents, handlePriceKeyDown)
                : <p className="font-display text-xl font-extrabold text-foreground">{format(rate)}</p>}
            </div>
          </div>
          <div>
            <p className="text-[12.5px] font-semibold text-foreground">{t("dailyRates.hotel.withPlanLabel")}</p>
            <div className="mt-2">
              {isEditing
                ? priceInput(hotelWithPlanInputRef, t("dailyRates.hotel.withPlanLabel"), priceCentsWithPlan, handlePriceWithPlanKeyDown)
                : <p className="font-display text-xl font-extrabold text-foreground">{format(data?.hotelNightlyRateWithDaycarePlan ?? null)}</p>}
            </div>
          </div>
        </div>
        {isEditing ? <p className="mt-2 text-[11.5px] text-color-light">{t("dailyRates.hotel.withPlanHint")}</p> : null}
        {isEditing ? <div className="mt-3 flex justify-end gap-2">
          <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => setEditing(null)}><X className="mr-1.5 h-3.5 w-3.5" />{t("common.cancel")}</Button>
          <Button size="sm" disabled={save.isPending} onClick={() => saveRate("hotel")}><Check className="mr-1.5 h-3.5 w-3.5" />{t("common.save")}</Button>
        </div> : null}
      </SectionCard>
    );
  };

  if (isLoading) return null;
  return <section>
    <SectionHeader tone="daycare" badge={t("dailyRates.badge")} title={t("dailyRates.title")} description={t("dailyRates.description")} />
    <div className="grid items-start gap-4 sm:grid-cols-2">
      {hasDaycare ? daycareCard() : null}
      {hasHotel ? hotelCard() : null}
    </div>
  </section>;
}
